import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, FolderOpen, Plus, ArrowUp, ArrowDown, X, Loader2, FileCheck2 } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import heic2any from "heic2any";

// Bundled locally (copied from node_modules/pdfjs-dist/build into /public) —
// no external CDN dependency for the worker.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

const ALLOWED_PAGE_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'image/heic', 'image/heif'];
const MAX_COMBINED_PDF_BYTES = 10 * 1024 * 1024; // 10MB, same limit as the legacy single-file upload
const LETTER_WIDTH_PT = 612;
const LETTER_HEIGHT_PT = 792;

let pageIdCounter = 0;
const nextPageId = () => `page_${Date.now()}_${pageIdCounter++}`;
let sourceKeyCounter = 0;
const nextSourceKey = () => `src_${Date.now()}_${sourceKeyCounter++}`;

const base64ToUint8Array = (base64) => {
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
};

const isHeicFile = (file) => {
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  return type === 'image/heic' || type === 'image/heif' || name.endsWith('.heic') || name.endsWith('.heif');
};

// Renders every page of a PDF to a small JPEG data URL for on-screen thumbnails
// ONLY — the original bytes passed in are never mutated and are kept separately
// so the real page content can be copied (not re-rastered) when saving.
const renderPdfThumbnails = async (bytes) => {
  // Pass a copy — pdf.js can transfer/detach the underlying ArrayBuffer of a
  // Uint8Array for performance, which would wipe out our kept-for-merging copy.
  const pdf = await pdfjsLib.getDocument({ data: bytes.slice() }).promise;
  const thumbUrls = [];
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const viewport = page.getViewport({ scale: 1.3 });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport }).promise;
    thumbUrls.push(canvas.toDataURL('image/jpeg', 0.7));
  }
  return thumbUrls;
};

// Draws through an <img> element so the browser's own EXIF-orientation handling
// is baked into the canvas pixels before we re-encode the page image.
const fileToOrientedBlob = (file) => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        resolve(blob);
      }, 'image/jpeg', 0.9);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read image')); };
    img.src = url;
  });
};

// Embeds an image page into the output PDF without forcing an unnecessary
// re-encode: JPEG/PNG go in as-is; anything else (e.g. GIF) is canvas-converted
// to JPEG first since pdf-lib can only embed JPEG or PNG bytes.
const embedImagePage = async (outDoc, blob) => {
  let usable = blob;
  if (blob.type !== 'image/jpeg' && blob.type !== 'image/png') {
    usable = await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        canvas.toBlob((b) => { URL.revokeObjectURL(url); resolve(b); }, 'image/jpeg', 0.9);
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not decode image')); };
      img.src = url;
    });
  }
  const buf = await usable.arrayBuffer();
  return usable.type === 'image/png' ? outDoc.embedPng(buf) : outDoc.embedJpg(buf);
};

// Merges the ordered page list into one PDF. Pages that came from a real PDF
// are copied with pdf-lib's copyPages (vector/text content preserved, never
// rasterized to JPEG); image pages are placed onto a Letter-sized page.
const composeMergedPdf = async (pages, filenameBase) => {
  const outDoc = await PDFDocument.create();
  const loadedSources = new Map();
  for (const page of pages) {
    if (page.type === 'pdf') {
      let srcDoc = loadedSources.get(page.sourceKey);
      if (!srcDoc) {
        srcDoc = await PDFDocument.load(page.pdfBytes);
        loadedSources.set(page.sourceKey, srcDoc);
      }
      const [copiedPage] = await outDoc.copyPages(srcDoc, [page.pageIndex]);
      outDoc.addPage(copiedPage);
    } else {
      const img = await embedImagePage(outDoc, page.blob);
      const imgRatio = img.width / img.height;
      const pageRatio = LETTER_WIDTH_PT / LETTER_HEIGHT_PT;
      let drawWidth, drawHeight;
      if (imgRatio > pageRatio) {
        drawWidth = LETTER_WIDTH_PT;
        drawHeight = LETTER_WIDTH_PT / imgRatio;
      } else {
        drawHeight = LETTER_HEIGHT_PT;
        drawWidth = LETTER_HEIGHT_PT * imgRatio;
      }
      const newPage = outDoc.addPage([LETTER_WIDTH_PT, LETTER_HEIGHT_PT]);
      newPage.drawImage(img, {
        x: (LETTER_WIDTH_PT - drawWidth) / 2,
        y: (LETTER_HEIGHT_PT - drawHeight) / 2,
        width: drawWidth,
        height: drawHeight,
      });
    }
  }
  const bytes = await outDoc.save();
  return new File([bytes], `${filenameBase}.pdf`, { type: 'application/pdf' });
};

/**
 * Multi-page capture/compose uploader for a single child compliance form.
 * - existingDoc present -> "Add Pages" flow: preloads the current document's pages.
 * - existingDoc null -> fresh upload; a single selected PDF, left untouched, is
 *   uploaded byte-for-byte as long as no pages are added/removed/reordered.
 */
const MultiPageDocumentUploader = ({ open, onOpenChange, formName, existingDoc, requiresDateOnForm, promptDateOnForm, onUpload }) => {
  const [pages, setPages] = useState([]);
  // A single real PDF file (fresh upload, or the current server document) that
  // can be sent unchanged as long as nothing about the page set has changed.
  const [untouchedSource, setUntouchedSource] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [processingPages, setProcessingPages] = useState(false);
  const [saving, setSaving] = useState(false);
  const cameraInputRef = useRef(null);
  const filesInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPages(prev => {
        prev.forEach(p => { if (p.type === 'image' && p.thumbUrl) URL.revokeObjectURL(p.thumbUrl); });
        return [];
      });
      setUntouchedSource(null);
      return;
    }
    if (!existingDoc || !existingDoc.data) {
      setPages([]);
      setUntouchedSource(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingExisting(true);
      try {
        const bytes = base64ToUint8Array(existingDoc.data);
        if (existingDoc.contentType === 'application/pdf') {
          const thumbUrls = await renderPdfThumbnails(bytes);
          const sourceKey = nextSourceKey();
          if (!cancelled) {
            setPages(thumbUrls.map((thumbUrl, idx) => ({ id: nextPageId(), type: 'pdf', thumbUrl, sourceKey, pdfBytes: bytes, pageIndex: idx })));
            setUntouchedSource({
              file: new File([bytes], existingDoc.filename || 'document.pdf', { type: 'application/pdf' }),
              pageCount: thumbUrls.length,
            });
          }
        } else {
          const blob = new Blob([bytes], { type: existingDoc.contentType || 'image/jpeg' });
          if (!cancelled) {
            setPages([{ id: nextPageId(), type: 'image', blob, thumbUrl: URL.createObjectURL(blob) }]);
            setUntouchedSource(null);
          }
        }
      } catch (error) {
        console.error("Failed to load existing document pages:", error);
        toast.error("Could not load the existing document's pages");
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, existingDoc]);

  const handleFilesSelected = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    // Restore allowed-type validation for every selected file. Reject the whole
    // batch on any invalid file so page order stays unambiguous — nothing is
    // uploaded here; everything only gets staged into the pages list below.
    const invalid = files.find(f => !ALLOWED_PAGE_FILE_TYPES.includes(f.type) && !isHeicFile(f));
    if (invalid) {
      toast.error(`${invalid.name}: unsupported file type. Use JPG, PNG, GIF, HEIC, or PDF.`);
      return;
    }

    // Only a single freshly-selected PDF, added as the very first page(s) of a
    // brand-new upload, can remain "untouched". Any other addition invalidates it.
    const isInitialSingleFreshLoad = !existingDoc && pages.length === 0 && files.length === 1 && files[0].type === 'application/pdf';
    setUntouchedSource(null);

    setProcessingPages(true);
    try {
      const newPages = [];
      let untouchedCandidate = null;
      for (const file of files) {
        if (file.type === 'application/pdf') {
          const buf = await file.arrayBuffer();
          const bytes = new Uint8Array(buf);
          const thumbUrls = await renderPdfThumbnails(bytes);
          const sourceKey = nextSourceKey();
          thumbUrls.forEach((thumbUrl, idx) => newPages.push({ id: nextPageId(), type: 'pdf', thumbUrl, sourceKey, pdfBytes: bytes, pageIndex: idx }));
          if (isInitialSingleFreshLoad) untouchedCandidate = { file, pageCount: thumbUrls.length };
        } else {
          let imageFile = file;
          if (isHeicFile(file)) {
            try {
              const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
              const convertedBlob = Array.isArray(converted) ? converted[0] : converted;
              imageFile = new File([convertedBlob], (file.name || 'photo').replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
            } catch (heicError) {
              console.error("HEIC conversion failed:", heicError);
              const err = new Error('heic-conversion-failed');
              err.handled = true;
              toast.error(`${file.name}: could not convert this iPhone photo (HEIC/HEIF). Please try again or use a JPEG.`);
              throw err;
            }
          }
          const orientedBlob = await fileToOrientedBlob(imageFile);
          newPages.push({ id: nextPageId(), type: 'image', blob: orientedBlob, thumbUrl: URL.createObjectURL(orientedBlob) });
        }
      }
      setPages(prev => [...prev, ...newPages]);
      if (untouchedCandidate) setUntouchedSource(untouchedCandidate);
    } catch (error) {
      if (!error.handled) {
        console.error("Failed to process selected pages:", error);
        toast.error("Could not process one or more selected files");
      }
    } finally {
      setProcessingPages(false);
    }
  };

  const movePage = (index, direction) => {
    setUntouchedSource(null);
    setPages(prev => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removePage = (index) => {
    setUntouchedSource(null);
    setPages(prev => {
      const removed = prev[index];
      if (removed && removed.type === 'image' && removed.thumbUrl) URL.revokeObjectURL(removed.thumbUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSaveComposedPages = async () => {
    if (pages.length === 0) {
      toast.error("Add at least one page first");
      return;
    }
    let dateOnForm;
    if (requiresDateOnForm) {
      dateOnForm = promptDateOnForm ? promptDateOnForm() : undefined;
      if (dateOnForm === null) return; // cancelled or invalid
    }
    setSaving(true);
    try {
      const filenameBase = (formName || 'document').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
      const file = (untouchedSource && pages.length === untouchedSource.pageCount)
        ? untouchedSource.file
        : await composeMergedPdf(pages, filenameBase);
      // Enforce the 10MB limit on the FINAL document, same as the legacy single-file upload.
      if (file.size > MAX_COMBINED_PDF_BYTES) {
        toast.error(`Combined PDF is ${(file.size / (1024 * 1024)).toFixed(1)}MB — must be under 10MB. Remove a page or lower photo quality and try again.`);
        return;
      }
      await onUpload(file, dateOnForm);
    } catch (error) {
      console.error("Failed to combine pages into a PDF:", error);
      toast.error("Could not combine pages into a PDF");
    } finally {
      setSaving(false);
    }
  };

  const busy = loadingExisting || processingPages || saving;

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-lg" data-testid="multi-page-upload-dialog">
        <DialogHeader>
          <DialogTitle className="break-words pr-6">{existingDoc ? `Add Pages — ${formName || ''}` : `Upload — ${formName || ''}`}</DialogTitle>
          <DialogDescription>
            {existingDoc ? "Review the existing pages, add any missing pages, then save." : "Take a photo or choose files for each page, then save them as one PDF."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => { handleFilesSelected(e.target.files); e.target.value = ''; }}
          />
          <input
            ref={filesInputRef}
            type="file"
            accept="image/*,application/pdf,.heic,.heif"
            multiple
            className="hidden"
            onChange={(e) => { handleFilesSelected(e.target.files); e.target.value = ''; }}
          />

          {pages.length > 0 && (
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-testid="add-another-page-label">
              Add Another Page
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => cameraInputRef.current?.click()}
              disabled={busy}
              data-testid="take-photo-btn"
            >
              <Camera className="w-4 h-4 mr-2" /> Take Photo
            </Button>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => filesInputRef.current?.click()}
              disabled={busy}
              data-testid="choose-files-btn"
            >
              <FolderOpen className="w-4 h-4 mr-2" /> Choose from Photos/Files
            </Button>
          </div>

          {(loadingExisting || processingPages) && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> {loadingExisting ? "Loading existing pages..." : "Processing..."}
            </div>
          )}

          {pages.length > 0 && (
            <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto p-1" data-testid="page-thumbnail-grid">
              {pages.map((page, index) => (
                <div key={page.id} className="relative border rounded-md overflow-hidden bg-gray-50" data-testid={`page-thumb-${index}`}>
                  <img src={page.thumbUrl} alt={`Page ${index + 1}`} className="w-full h-24 object-contain bg-white" />
                  <span className="absolute top-1 left-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
                    {index + 1}
                  </span>
                  <div className="absolute top-1 right-1 flex gap-0.5">
                    <button
                      type="button"
                      onClick={() => movePage(index, -1)}
                      disabled={index === 0}
                      className="bg-white/90 hover:bg-white rounded p-0.5 disabled:opacity-30"
                      title="Move up"
                      data-testid={`move-up-${index}`}
                    >
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => movePage(index, 1)}
                      disabled={index === pages.length - 1}
                      className="bg-white/90 hover:bg-white rounded p-0.5 disabled:opacity-30"
                      title="Move down"
                      data-testid={`move-down-${index}`}
                    >
                      <ArrowDown className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removePage(index)}
                      className="bg-white/90 hover:bg-red-100 rounded p-0.5"
                      title="Remove page"
                      data-testid={`remove-page-${index}`}
                    >
                      <X className="w-3 h-3 text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSaveComposedPages}
            disabled={busy || pages.length === 0}
            data-testid="save-pages-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileCheck2 className="w-4 h-4 mr-2" />}
            Save {pages.length > 1 ? `${pages.length} Pages as PDF` : "Document"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MultiPageDocumentUploader;
